/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the variant brain (Masters Ladder R3, §2.2–2.4).

   ONE place turns a TipTap document back into layer operations. The
   canvas NEVER re-implements composition: `compose()` (the R1 shared
   brain) says what a variant LOOKS like, and `deriveLayer()` here says
   what the user's typing MEANT. Both directions are pure, so both are
   unit-tested without an editor (`variantOps.test.ts`).

   The round trip, in three sentences:

     1. `composeVariant()` stacks the client's layer on the live core and
        `variantDocBlocks()` hands the canvas ONE ordinary block list —
        overrides already applied, hidden blocks flagged as ghosts.
     2. The user types. Every doc change comes back here as JSON.
     3. `deriveLayer()` walks that JSON against the BASELINE (the same
        master WITHOUT the layer): a baseline block that changed is an
        override, one flagged ghost is a hide, anything whose id the
        baseline never had is an add anchored after the last baseline
        block above it. Editing a block back to its core text drops the
        override by construction — "revert" needs no special case.

   coreContent is NEVER written from a client variant. That is the law
   (§2.2 "paint on the active layer"), and it is enforced by the Studio
   handing this module the doc instead of `draft.onBlocksChanged`.
   ═══════════════════════════════════════════════════════════════ */

import type { JSONContent } from '@tiptap/core';
import type { BlockOverride, ClientLayer, ContentBlock, LayerAdd } from '../../shared/types';
import { compose, hashBlock, makeOverrideBase } from '../../shared/spec-compose';
import type { ComposeResult } from '../../shared/spec-compose';
import { blocksToDoc, docToBlocks } from '../designer/schema';

/* ─── Ghost marking ─────────────────────────────────────────────
   A hidden block stays IN the document as a dashed stub, so hiding and
   restoring are ordinary undoable transactions on one timeline (§2.7)
   instead of a rebuild that would eat the user's undo history. The flag
   rides in the node's `meta` under a `__`-prefixed key, which
   `docToBlocks` strips on the way out — so it can never leak into a
   saved block, an override payload, or an export. ─────────────── */

export const GHOST_KEY = '__ghost';

/** Read the ghost flag off a raw ProseMirror node's JSON. */
export function isGhostNode(node: JSONContent): boolean {
  const meta = (node?.attrs?.meta || {}) as Record<string, unknown>;
  return meta[GHOST_KEY] === true;
}

/* ─── Baseline ─────────────────────────────────────────────────── */

export interface BaselineEntry {
  /** The base block exactly as the master stores it — what an override's
   *  snapshot is captured from and what "revert to core" restores. */
  raw: ContentBlock;
  /** Hash of the base AFTER a doc round trip. The editor's output is always
   *  round-tripped, so this is the honest yardstick for "the user actually
   *  changed something" — a lossless-but-normalising conversion (explicit
   *  `bold: false`, run folding) must never read as an edit. */
  normHash: string;
}

export interface VariantBaseline {
  /** Baseline block ids in document order. */
  order: string[];
  byId: Map<string, BaselineEntry>;
}

/** Normalise blocks the way the editor does, so two shapes of the same
 *  content compare equal. */
export function normalizeBlocks(blocks: ContentBlock[]): ContentBlock[] {
  if (!blocks.length) return [];
  return docToBlocks(blocksToDoc(blocks));
}

/**
 * The master WITHOUT the layer — everything a variant's edits are measured
 * against. Built through `compose()` so the shared brain, not this module,
 * decides what a master's document IS.
 */
export function buildBaseline(core: ContentBlock[], masterId: string): VariantBaseline {
  const result = compose({ id: masterId, coreContent: core });
  const raws = result.blocks.map((c) => c.block);
  const norm = normalizeBlocks(raws);
  const byId = new Map<string, BaselineEntry>();
  const order: string[] = [];
  result.blocks.forEach((c, i) => {
    const id = c.block.id;
    if (!id || byId.has(id)) return;
    order.push(id);
    byId.set(id, { raw: raws[i], normHash: hashBlock(norm[i] || raws[i]) });
  });
  return { order, byId };
}

/* ─── Composing a variant for the canvas ──────────────────────────── */

/** The composed variant. */
export function composeVariant(
  core: ContentBlock[],
  masterId: string,
  layer?: ClientLayer
): ComposeResult {
  return compose({ id: masterId, coreContent: core }, layer);
}

/**
 * The block list the canvas loads for a variant: one ordinary document, with
 * hidden blocks carrying the ghost flag. The sheet ALWAYS reads as a complete
 * document — never layer plumbing (§2.1).
 */
export function variantDocBlocks(result: ComposeResult): ContentBlock[] {
  return result.blocks.map((c) =>
    c.hidden ? ({ ...c.block, [GHOST_KEY]: true } as ContentBlock) : c.block
  );
}

/* ─── Provenance, for the canvas decorations ───────────────────── */

/** What the canvas needs to paint provenance. Cheap to rebuild — it is
 *  recomputed after every derive without recomposing the document. */
export interface VariantView {
  layerId: string;
  clientName: string;
  baselineIds: Set<string>;
  overriddenIds: Set<string>;
  staleIds: Set<string>;
  /** Baseline block id -> the group the OFFICE MASTER put it in. Only blocks
   *  the master actually grouped appear here, and it is what the guard stands
   *  on: a group that came down from the master is the master's to change
   *  (`variantPlugin`, reason 'group'), while a variant is free to group its
   *  own blocks and any master block the master left ungrouped. */
  masterGroupIds: Map<string, string>;
}

/** Which overrides no longer match the core they were written against. */
export function staleOverrideBlockIds(
  layer: ClientLayer | null | undefined,
  baseline: VariantBaseline
): Set<string> {
  const out = new Set<string>();
  for (const o of layer?.overrides || []) {
    const base = baseline.byId.get(o.blockId);
    if (!base || !o.base?.hash) continue;
    if (hashBlock(base.raw) !== o.base.hash) out.add(o.blockId);
  }
  return out;
}

/** The groups the office master itself declared, by block id. */
function masterGroupIds(baseline: VariantBaseline): Map<string, string> {
  const out = new Map<string, string>();
  for (const [id, entry] of baseline.byId) {
    const gid = (entry.raw.groupId || '').trim();
    if (gid) out.set(id, gid);
  }
  return out;
}

export function buildVariantView(
  layer: ClientLayer,
  baseline: VariantBaseline,
  clientName: string
): VariantView {
  return {
    layerId: layer.id,
    clientName,
    baselineIds: new Set(baseline.order),
    overriddenIds: new Set((layer.overrides || []).map((o) => o.blockId)),
    staleIds: staleOverrideBlockIds(layer, baseline),
    masterGroupIds: masterGroupIds(baseline),
  };
}

/* ─── Doc → layer ops ──────────────────────────────────────────── */

export interface DeriveContext {
  baseline: VariantBaseline;
  /** The layer as it stood. Carried forward: override ids, base snapshots
   *  (so "core has changed" survives another keystroke), creation stamps, and
   *  anything the current document cannot see (dormant + orphan overrides). */
  previous: ClientLayer;
  updatedBy?: string;
  now?: string;
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/** Mint a variant's layer. `clientId` is legacy (migration 044) — Cohesion R1
 *  Phase 3 variants are freeform-named, so new callers pass `undefined`; kept
 *  as a parameter only so the pre-existing test fixtures still compile. */
export function newClientLayer(
  clientId: string | undefined,
  name: string,
  masterId: string,
  userName?: string
): ClientLayer {
  const now = new Date().toISOString();
  return {
    id: newId('layer'),
    ...(clientId ? { clientId } : {}),
    name,
    masterId,
    adds: [],
    overrides: [],
    hides: [],
    createdAt: now,
    updatedAt: now,
    ...(userName ? { updatedBy: userName } : {}),
  };
}

/**
 * Duplicate a variant's authorship into a fresh layer — a starting point, not
 * a link. Adds, overrides and hides are deep-copied; every added block (and
 * any anchor pointing at one of THIS variant's own adds) gets a fresh id, so
 * the new variant's own edits can never collide with block ids the source
 * variant still owns. Overrides and hides anchor into the office master's
 * ids, which never change on a duplicate, so those are copied as-is.
 */
export function duplicateVariantLayer(
  source: ClientLayer,
  name: string,
  masterId: string,
  userName?: string
): ClientLayer {
  const now = new Date().toISOString();
  const idMap = new Map<string, string>();
  for (const add of source.adds || []) idMap.set(add.block.id, newId('blk'));
  const adds: LayerAdd[] = (source.adds || []).map((add) => ({
    ...add,
    block: { ...add.block, id: idMap.get(add.block.id)! },
    ...(add.afterBlockId && idMap.has(add.afterBlockId)
      ? { afterBlockId: idMap.get(add.afterBlockId) }
      : {}),
  }));
  const overrides: BlockOverride[] = (source.overrides || []).map((o) => ({
    ...o,
    id: newId('ov'),
    createdAt: now,
    updatedAt: now,
  }));
  return {
    id: newId('layer'),
    name,
    masterId,
    adds,
    overrides,
    hides: [...(source.hides || [])],
    createdAt: now,
    updatedAt: now,
    ...(userName ? { updatedBy: userName } : {}),
  };
}

/** The content fields an override carries — the SAME set the compose brain
 *  hashes, so a payload can never disagree with staleness. */
function contentPayload(block: ContentBlock) {
  return makeOverrideBase(block).fields;
}

/** One node's JSON → the ContentBlock the editor would save for it. */
function nodeToBlock(node: JSONContent): ContentBlock | null {
  const out = docToBlocks({ type: 'doc', content: [node] });
  return out[0] || null;
}

/**
 * Turn the live document into the client's layer.
 *
 * Attribution is by BLOCK ID, never by text matching: the baseline knows
 * every id the office master owns, so anything else in the document is this
 * client's own writing. Splitting a core block therefore falls out correctly
 * on its own — the first half keeps the core id (→ override) and the second
 * half is minted a fresh one by `UniqueBlockIds` (→ add).
 */
export function deriveLayer(doc: JSONContent, ctx: DeriveContext): ClientLayer {
  const { baseline, previous } = ctx;
  const now = ctx.now || new Date().toISOString();
  const prevOverrides = new Map<string, BlockOverride>();
  for (const o of previous.overrides || []) prevOverrides.set(o.blockId, o);
  const prevHides = new Set(previous.hides || []);

  const hides: string[] = [];
  const adds: LayerAdd[] = [];
  const overrides: BlockOverride[] = [];
  const seen = new Set<string>();

  let anchor: string | undefined;
  let index = 0;

  for (const node of doc?.content || []) {
    const block = nodeToBlock(node);
    if (!block) continue;
    const id = block.id;
    const base = baseline.byId.get(id);

    // A duplicate of a baseline id is a split or a duplicate mid-flight;
    // `UniqueBlockIds` re-mints it on the very next tick, so treat the second
    // copy as this client's block rather than as the core one moving.
    if (base && !seen.has(id)) {
      seen.add(id);
      anchor = id;
      index++;
      if (isGhostNode(node)) hides.push(id);

      const prev = prevOverrides.get(id);
      // Content AND group: `hashBlock` is deliberately content-only (grouping
      // must never read as a content edit anywhere else), so the one place a
      // variant's own grouping has to be noticed asks for it explicitly.
      const sameGroup = (block.groupId || '') === (base.raw.groupId || '');
      if (sameGroup && hashBlock(block) === base.normHash) continue; // identical to core
      overrides.push({
        id: prev?.id || newId('ov'),
        blockId: id,
        payload: contentPayload(block),
        // The snapshot is captured ONCE, from the raw core block, and then
        // carried: re-capturing on every keystroke would clear the "core has
        // changed" indicator the moment the client touched the block again.
        base: prev?.base || makeOverrideBase(base.raw),
        createdAt: prev?.createdAt || now,
        updatedAt: now,
        ...(ctx.updatedBy ? { updatedBy: ctx.updatedBy } : {}),
      });
      continue;
    }

    adds.push({ block, afterBlockId: anchor, index });
    index++;
  }

  // Anything the baseline has that the document does not: never invent a
  // hide, never drop a hide. The composition puts it back on the next render.
  for (const id of baseline.order) {
    if (!seen.has(id) && prevHides.has(id)) hides.push(id);
  }
  // Overrides aimed at blocks this composition does not contain (the office
  // master deleted the block). `compose()` reports them as orphans; losing
  // them here would be a silent data loss.
  for (const o of previous.overrides || []) {
    if (!baseline.byId.has(o.blockId)) overrides.push(o);
  }

  return {
    ...previous,
    adds,
    overrides,
    hides,
    updatedAt: now,
    ...(ctx.updatedBy ? { updatedBy: ctx.updatedBy } : {}),
  };
}

/** Value equality for two layers — the Studio only marks the draft dirty when
 *  the layer really moved (a caret keystroke that changes nothing must not). */
export function sameLayer(a: ClientLayer, b: ClientLayer): boolean {
  const strip = (l: ClientLayer) =>
    JSON.stringify({
      id: l.id,
      clientId: l.clientId,
      name: l.name,
      adds: l.adds || [],
      overrides: (l.overrides || []).map((o) => ({ ...o, updatedAt: '', createdAt: '' })),
      hides: [...(l.hides || [])].sort(),
    });
  return strip(a) === strip(b);
}
